import axios from 'axios';

import { clearTokens, getAccessToken } from './authTokens';

// API_BASE_URL is controlled via .env file (REACT_APP_SSD_FRONT__API_URL):
// - Local development: set to 'http://localhost:8001' (direct backend connection)
// - Docker/production: leave empty or unset to use '/api' (nginx proxy)
const API_BASE_URL = process.env.REACT_APP_SSD_FRONT__API_URL || '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

// Attach Bearer token from the localStorage-backed token module.
apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Centralized error handling. The gateway does not expose refresh — on 401
// we clear the token and send the browser back to the app root; the shell
// then renders the login screen and re-runs SSO.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    let errorMessage = 'An error occurred';
    if (error.response?.data) {
      const data = error.response.data;
      if (typeof data === 'string') {
        errorMessage = data;
      } else if (data.detail) {
        if (Array.isArray(data.detail)) {
          errorMessage = data.detail.map((e) => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof data.detail === 'object') {
          errorMessage = data.detail.msg || data.detail.message || JSON.stringify(data.detail);
        } else {
          errorMessage = data.detail;
        }
      } else if (data.message) {
        errorMessage = data.message;
      } else if (data.error) {
        errorMessage = data.error;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    const requestUrl = error.config?.url || '';
    const isAuthEndpoint = requestUrl.startsWith('/auth/') || requestUrl.includes('/auth/');
    const status = error.response?.status;

    if ((status === 401 || status === 403) && !isAuthEndpoint) {
      clearTokens();
      window.location.replace('/');
      return new Promise(() => {});
    }

    error.userMessage = errorMessage;
    return Promise.reject(error);
  }
);

export const api = {
  // Auth
  getAuthConfig: () => apiClient.get('/auth/config'),
  exchangeAuthCode: (authCode) => apiClient.post('/auth/token', { auth_code: authCode }),
  devLogin: (userId, email) => apiClient.post('/auth/login', { user_id: userId, email }),
  getMe: () => apiClient.get('/auth/me'),
  logout: (returnUrl) => apiClient.post('/auth/logout', returnUrl ? { return_url: returnUrl } : {}),

  // File operations
  uploadFile: (userId, formData) => apiClient.post(`/files/${userId}/upload`, formData),
  listFiles: (userId) => apiClient.get(`/files/${userId}/list`),
  downloadFile: (userId, fileName, fileType, folderPath = null, defaultRules = false) =>
    apiClient.post(
      `/files/${userId}/download`,
      { file_name: fileName, file_type: fileType, folder_path: folderPath, default_rules: defaultRules },
      { responseType: 'blob' }
    ),
  deleteFile: (userId, fileName, folderPath, fileType) =>
    apiClient.post(`/files/${userId}/delete`, {
      file_name: fileName,
      folder_path: folderPath,
      file_type: fileType,
    }),

  // Queue operations
  getDocxFilters: (fileName) =>
    apiClient.get('/queue/docx-filters', { params: { file_name: fileName } }),
  submitToQueue: (payload) => apiClient.post('/queue/jobs/from-storage', payload),
  cancelJob: (jobId) => apiClient.post(`/queue/jobs/${jobId}/cancel`),
  getReports: () => apiClient.get('/queue/reports'),
  downloadJobResults: (jobId) =>
    apiClient.get(`/queue/jobs/${jobId}/results`, { responseType: 'blob' }),

  // Rules and Parser Configs
  getAllRules: () => apiClient.get('/rules/all'),
  getAvailableArchitectures: () => apiClient.get('/parser-configs/architectures'),

  // Metrics
  getMetrics: (startDate, endDate) =>
    apiClient.get('/metrics', {
      params: { start_date: startDate, end_date: endDate },
    }),

  // User observability
  getUserObservability: () => apiClient.get('/user-observability'),
};

export { API_BASE_URL };
export default apiClient;
